import React from "react";
import { Navigate, RouteObject } from "react-router-dom";

import { PageError } from "../../components/Error";

import AdminEmployees from "../../pages/AdminEmployees";
import AIInsightsSettings, { aIInsightsSettingsLoader } from "../../pages/Administration/AIAssistants/AIInsights";
import AIRephraseAssistant, {
	aIRephraseAssistantSettingsLoader,
} from "../../pages/Administration/AIAssistants/AIRephrase";
import AuthenticationPage from "../../pages/Administration/Authentication";
import ViewApiKey from "../../pages/Administration/Authentication/pages/APIKey/ViewApiKey";
import { companySettingsLoader } from "../../pages/Administration/Authentication/pages/CompanySettings";
import CoachingSettings, { coachingSettingsLoader } from "../../pages/Administration/Coaching";
import CompensationAdmin, { compensationSettingsLoader } from "../../pages/Administration/Compensation";
import CompetenciesAdmin, { competenciesLoader } from "../../pages/Administration/Competencies";
import ConversationalAISettings, { conversationalAISettingsLoader } from "../../pages/Administration/ConversationalAI";
import EngagementAdminSettings, { engagementSettingsLoader } from "../../pages/Administration/Engagement";
import AdminSettingsHomePage, { adminSettingsHomeLoader } from "../../pages/Administration/Home";
import ImportFieldHistory from "../../pages/Administration/ImportFieldHistory";
import Log from "../../pages/Administration/Log";
import LogDetails from "../../pages/Administration/LogDetails";
import MeetingsIntegrations, {
	meetingsIntegrationsSettingsLoader,
} from "../../pages/Administration/MeetingsIntegrations";
import TeamsIntegrationSetup from "../../pages/Administration/MicrosoftTeamsIntegration";
import NotificationsTemplate from "../../pages/Administration/NotificationsTemplate";
import AdminReviewTemplateDetail from "../../pages/Administration/ReviewTemplateDetail";
import EditForm, { adminReviewFormLoader } from "../../pages/Administration/Reviews/EditForm";
import AdminReviewsForms, { adminReviewsFormsLoader } from "../../pages/Administration/Reviews/Forms";
import RolesAdmin, { rolesLoader } from "../../pages/Administration/Roles";
import ViewSyncLog, { scimLogLoader } from "../../pages/Administration/SCIMSettings/ScimLogs";
import ViewSCIMToken from "../../pages/Administration/SCIMSettings/ViewSCIMToken";
import SCIMSettings from "../../pages/Administration/SCIMSettings/index";
import SftpSettings from "../../pages/Administration/Sftp";
import SetUpSFTP from "../../pages/Administration/Sftp/SftpSettings";
import SignatureRequestsAdmin, { signatureRequestsLoader } from "../../pages/Administration/SignatureRequests";
import WorkflowsAdmin, { workflowsLoader } from "../../pages/Administration/Workflows";
import Billing, { billingLoader } from "../../pages/Billing";
import BillingErrorPage from "../../pages/Billing/BillingErrorPage";
import CompanyDirectory from "../../pages/CompanyDirectory";
import CompanyFeedback from "../../pages/CompanyFeedback";
import CompanyMeetingsPage from "../../pages/CompanyMeetings";
import CompanyMeetingsCoveragePage from "../../pages/CompanyMeetingsCoverage";
import CompanyResources from "../../pages/CompanyResources";
import { CompensationLanding } from "../../pages/Compensation";
import CompensationCreateWizard, { compensationCreateLoader } from "../../pages/Compensation/CreateWizard";
import BudgetReviews from "../../pages/Compensation/CycleDetails/BudgetReviews/BudgetReviews";
import CycleDetailsLayout from "../../pages/Compensation/CycleDetails/CycleDetailsLayout";
import CycleOverview from "../../pages/Compensation/CycleDetails/CycleOverview/CycleOverview";
import MyDecisions from "../../pages/Compensation/CycleDetails/MyDecisions";
import CompanyCompetencies from "../../pages/Competencies/Company";
import EmployeeCompetencies from "../../pages/Competencies/Employee";
import Dashboard from "../../pages/Dashboard";
import DeactivateEmployees from "../../pages/DeactivateEmployees";
import Documents from "../../pages/Documents";
import EditEmployee, { addEmployeeLoader } from "../../pages/EditEmployee/EditEmployee";
import EmployeeDetailsErrorPage from "../../pages/EmployeeDetails/EmployeeDetailsErrorPage";
import EmployeeDetailsLayout, { viewEmployeeDetailsLoader } from "../../pages/EmployeeDetails/EmployeeDetailsLayout";
import EmployeeDetailsView from "../../pages/EmployeeDetails/EmployeeDetailsView";
import EmployeeInsights from "../../pages/EmployeeDetails/EmployeeInsights";
import ViewFieldHistory from "../../pages/EmployeeDetails/ViewFieldHistory";
import EngagementCohorts from "../../pages/Engagement/pages/Cohorts";
import EngagementFactors from "../../pages/Engagement/pages/Factors";
import EngagementOverview from "../../pages/Engagement/pages/Overview";
import EngagementQuestions from "../../pages/Engagement/pages/Questions";
import EngagementEnps from "../../pages/Engagement/pages/eNPS";
import ExtendSessionPage from "../../pages/ExtendSession";
import Feedback from "../../pages/Feedback";
import CompanyGoalsPage from "../../pages/Goals/Company";
import GoalsCreate from "../../pages/Goals/Create";
import EmployeeGoals from "../../pages/Goals/Employee";
import SingleGoal from "../../pages/Goals/Single";
import ViewCreateGoalsJob from "../../pages/Goals/ViewJob";
import ViewGroup from "../../pages/Group";
import IntegrationsMain from "../../pages/Integrations/Main";
import ViewAllJobs from "../../pages/Jobs/All";
import ViewJob from "../../pages/Jobs/Single";
import MeetingDetails, { meetingDetailsLoader } from "../../pages/Meetings/MeetingDetails";
import MeetingsTable from "../../pages/Meetings/MeetingsTable";
import WorkspacesTable, { workspacesTableLoader } from "../../pages/Meetings/WorkspacesTable";
import OrgChartRedirect from "../../pages/OrgChart/OrgChartRedirect";
import ReportAnalyticsChat from "../../pages/ReportAnalytics/pages/Chat";
import ReportAnalytics from "../../pages/ReportAnalytics/pages/Home";
import AnalyticsReport from "../../pages/ReportAnalytics/pages/Report";
import ReviewCycleAddForms from "../../pages/ReviewCycle/AddForms";
import ReviewCycleAddSubjects from "../../pages/ReviewCycle/AddSubjects";
import ReviewCycleAssignmentLog, { assignmentLogLoader } from "../../pages/ReviewCycle/AssignmentLog";
import CreateReviewCycle from "../../pages/ReviewCycle/Create";
import ReviewCycleDetails, { reviewCycleDetailsLoader } from "../../pages/ReviewCycle/Details";
import EditReviewCycle from "../../pages/ReviewCycle/Edit";
import ReviewCycleEdit360FormSettings from "../../pages/ReviewCycle/Edit360FormSettings";
import EditAutoflow from "../../pages/ReviewCycle/EditAutoflow";
import CompanyReviews from "../../pages/Reviews/Company";
import PerformanceGraphsWrapper from "../../pages/Reviews/Company/SubPages/PerformanceGraphs";
import Review from "../../pages/Reviews/Review";
import UserReviews from "../../pages/Reviews/UserReviews";
import SurveyPage, { surveyLoader } from "../../pages/Surveys/pages/SurveyAuthoring";
import SurveyDetailsPage, { surveyDetailsLoader } from "../../pages/Surveys/pages/SurveyDetails";
import SurveysPage, { surveysLoader } from "../../pages/Surveys/pages/Surveys";
import TemplateLibraryPage from "../../pages/TemplateLibrary";
import UserFeedback from "../../pages/UserFeedback";
import UserSettings from "../../pages/UserSettings";
import { meetingsIntegrationsViewLoader } from "../../pages/UserSettings/Views/MeetingsIntegrations";
import ViewReview from "../../pages/ViewReview";
import ViewReviewForm from "../../pages/ViewReviewForm";
import AllTodos from "../../pages/Workflows/AllTodos";
import AllWorkflows from "../../pages/Workflows/AllWorkflows";
import LaunchWorkflow from "../../pages/Workflows/LaunchWizard";
import WorkflowTemplateEditor from "../../pages/Workflows/TemplateEditor";
import WorkflowDetails from "../../pages/Workflows/WorkflowDetails";

import { readTargetCompanyId } from "../../utils/targetCompanyId";

import { MainNavSections } from "../../constants/MainNavSections";
import RouterTags from "../../constants/RouterTags";
import App from "../app";
import { ReactRouterCompatibilityLoader } from "./compatibility";

// These loaders only prime the react-query cache; their pages refetch client-side from the
// URL params. Re-running them on search-param-only navigations puts a fetch inside every
// keystroke of a URL-driven search box, and on react-router 7 each of those navigations is
// an interruptible transition that cannot commit until the loader resolves, so fast typing
// keeps the page on stale data (v29.33.0 E2E fallout).
const revalidateOnlyOnPathChange = ({ currentUrl, nextUrl }: { currentUrl: URL; nextUrl: URL }) =>
	currentUrl.pathname !== nextUrl.pathname;

// Employee details' loader branches on the cross-company params (companyId / nested from chain),
// so it must also re-run when the resolved target company changes, not just the pathname.
const revalidateOnPathOrTargetCompanyChange = ({ currentUrl, nextUrl }: { currentUrl: URL; nextUrl: URL }) =>
	currentUrl.pathname !== nextUrl.pathname ||
	readTargetCompanyId(currentUrl.search) !== readTargetCompanyId(nextUrl.search);

const routes = [
	{ path: "*", element: <App />, errorElement: <PageError /> },
	{
		path: "/",
		element: <App />,
		errorElement: <PageError />,
		loadingElement: <ReactRouterCompatibilityLoader />,
		children: [
			{
				index: true,
				element: <Dashboard />,
				handle: { navPath: [MainNavSections.PERFORMANCE, "dashboard"] },
			},
			{
				path: "user/:userID",
				element: <Dashboard />,
				handle: {
					tags: [RouterTags.USER_PAGE],
					navPath: [MainNavSections.PERFORMANCE, "dashboard"],
				},
			},
			{
				path: "employee-page/:userID",
				element: <Dashboard />,
				handle: {
					tags: [RouterTags.USER_PAGE],
					navPath: [MainNavSections.PERFORMANCE, "dashboard"],
				},
			},
			{
				path: "view-feedback/:feedbackId",
				element: <Feedback />,
				handle: {
					tags: [],
					navPath: [MainNavSections.PERFORMANCE, "feedback"],
					legacyPageName: "viewFeedback",
				},
			},
			{
				path: "feedback/:userId/:view?",
				element: <UserFeedback />,
				handle: {
					tags: [RouterTags.USER_PAGE, RouterTags.USES_TEMPLATE_AND_REACT],
					navPath: [MainNavSections.PERFORMANCE, "feedback"],
					legacyPageName: "userFeedback",
				},
			},
			{
				path: "directory",
				element: <CompanyDirectory />,
				handle: { navPath: [MainNavSections.COMPANY, "directory"] },
			},
			{
				path: "review-cycle/create",
				element: <CreateReviewCycle />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.ANALYTICS] },
			},
			{
				path: "review-cycle/:cycleId",
				element: <ReviewCycleDetails />,
				handle: { navPath: [MainNavSections.COMPANY, "reviews"] },
				loader: reviewCycleDetailsLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "review-cycle/:cycleId/edit",
				element: <EditReviewCycle />,
				handle: { tags: [RouterTags.NO_FEEDBACK], legacyPageName: "ReviewCycleEdit" },
			},
			{
				path: "review-cycle/:cycleId/add",
				element: <ReviewCycleAddSubjects />,
				handle: { tags: [RouterTags.NO_FEEDBACK], legacyPageName: "ReviewCycleAddSubjects" },
			},
			{
				path: "review-cycle/:cycleId/add-forms",
				element: <ReviewCycleAddForms />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.COMPANY, "reviews"] },
			},
			{
				path: "review-cycle/:cycleId/edit-360-form-settings",
				element: <ReviewCycleEdit360FormSettings />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.COMPANY, "reviews"] },
			},
			{
				path: "review-cycle/:cycleId/edit-automation",
				element: <EditAutoflow />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.COMPANY] },
			},
			{
				path: "review-cycle/:cycleId/assignment-log",
				element: <ReviewCycleAssignmentLog />,
				handle: { navPath: [MainNavSections.COMPANY, "reviews"] },
				loader: assignmentLogLoader,
			},
			{
				path: "administration",
				element: <AdminSettingsHomePage />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV, RouterTags.USES_TEMPLATE_AND_REACT],
					navPath: [MainNavSections.ADMINISTRATION, "home"],
				},
				loader: adminSettingsHomeLoader,
			},
			{
				path: "super-user-admin/employees/:companyId/",
				element: <AdminEmployees />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "employees"],
				},
			},
			{
				path: "administration/employees",
				element: <AdminEmployees />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "employees"],
				},
			},
			{
				path: "super-user-admin/sftp/:companyId",
				element: <SftpSettings />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "sftp"],
				},
			},
			{
				path: "administration/sftp",
				element: <SftpSettings />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "sftp"],
				},
			},
			{
				path: "administration/sftp/create",
				element: <SetUpSFTP />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "sftp", "create"],
				},
			},
			{
				path: "administration/sftp/edit/:integrationID",
				element: <SetUpSFTP />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "sftp", "create"],
				},
			},
			{
				path: "super-user-admin/microsoft-teams-setup/:companyId",
				element: <TeamsIntegrationSetup />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "microsoft-teams-setup"],
				},
			},
			{
				path: "administration/microsoft-teams-setup",
				element: <TeamsIntegrationSetup />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "microsoft-teams-setup"],
				},
			},
			{
				path: "super-user-admin/sftp/:companyId",
				element: <Log />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "sftp"],
				},
			},
			{
				path: "administration/sync-log",
				element: <Log />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "sync-log"],
				},
			},
			{
				path: "administration/sync-log/:integrationId",
				element: <LogDetails />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "sync-log", "view-sync-log"],
				},
			},
			{
				path: "administration/bulk-deactivate",
				element: <DeactivateEmployees />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "bulk-deactivate"],
				},
			},
			{
				path: "administration/import-field-history",
				element: <ImportFieldHistory />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "import-field-history"],
				},
			},
			{
				path: "administration/authentication-settings/:settingsPage?",
				element: <AuthenticationPage />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "authentication-settings"],
				},
				loader: async ({ params }) => {
					if (params.settingsPage === "company") {
						return companySettingsLoader();
					}
					return null;
				},
			},
			{
				path: "administration/authentication-settings/api/:apiId",
				element: <ViewApiKey />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "authentication-settings", "api", "view-api-key"],
				},
			},
			{
				path: "administration/scim/",
				element: <SCIMSettings />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "scim"],
				},
			},
			{
				path: "administration/scim/:scimId",
				element: <ViewSCIMToken />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "scim", "view-scim-token"],
				},
			},
			{
				path: "administration/scim/:scimId/sync-log",
				element: <ViewSyncLog />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "scim", "sync-log"],
				},
				loader: scimLogLoader,
			},
			{
				path: "administration/ai-assistants/ai-rephrase",
				element: <AIRephraseAssistant />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "ai-assistants", "ai-rephrase"],
				},
				loader: aIRephraseAssistantSettingsLoader,
			},
			{
				path: "administration/ai-assistants/ai-insights",
				element: <AIInsightsSettings />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "ai-assistants", "ai-insights"],
				},
				loader: aIInsightsSettingsLoader,
			},
			{
				path: "administration/coaching",
				element: <CoachingSettings />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "coaching"],
				},
				loader: coachingSettingsLoader,
			},
			{
				path: "administration/conversational-ai",
				element: <ConversationalAISettings />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "conversational-ai"],
				},
				loader: conversationalAISettingsLoader,
			},
			{
				path: "administration/engagement",
				element: <EngagementAdminSettings tab="main" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "engagement", "main"],
				},
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/cohorts",
				element: <EngagementAdminSettings tab="cohorts" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "engagement", "cohorts"],
				},
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/create-cohort",
				element: <EngagementAdminSettings tab="create-cohort" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "engagement", "cohorts", "create-cohort"],
				},
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/duplicate-cohort/:cohortID",
				element: <EngagementAdminSettings tab="duplicate-cohort" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "engagement", "cohorts", "duplicate-cohort"],
				},
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/cohorts/:cohortID/details",
				element: <EngagementAdminSettings tab="edit-cohort-details" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "engagement", "cohorts", "edit-cohort-details"],
				},
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/cohorts/:cohortID/add-employees",
				element: <EngagementAdminSettings tab="edit-cohort-employees-add-employees" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [
						MainNavSections.ADMINISTRATION,
						"engagement",
						"cohorts",
						"edit-cohort-employees-add-employees",
					],
				},
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/cohorts/:cohortID",
				element: <EngagementAdminSettings tab="edit-cohort-employees" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "engagement", "cohorts", "edit-cohort-employees"],
				},
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/questions",
				element: <EngagementAdminSettings tab="questions" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "engagement", "questions"],
				},
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/excluded-employees",
				element: <EngagementAdminSettings tab="excluded-employees" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "engagement", "excluded-employees"],
				},
				loader: engagementSettingsLoader,
			},

			{
				path: "administration/competencies",
				element: <CompetenciesAdmin tab="competencies" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV, RouterTags.COMPETENCIES_ADMIN],
					navPath: [MainNavSections.ADMINISTRATION, "competencies"],
				},
				loader: competenciesLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "administration/competencies/create",
				element: <CompetenciesAdmin tab="create" />,
				handle: {
					// PY-12512: create/edit are full-focus pages; hide the admin sidebar.
					tags: [RouterTags.NO_FEEDBACK, RouterTags.HIDE_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "competencies"],
				},
				loader: competenciesLoader,
			},
			{
				path: "administration/competencies/:competencyId/edit",
				element: <CompetenciesAdmin tab="edit" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.HIDE_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "competencies"],
				},
				loader: competenciesLoader,
			},

			{
				path: "administration/roles",
				element: <RolesAdmin tab="roles" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "roles"],
				},
				loader: rolesLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "administration/signature-requests",
				element: <SignatureRequestsAdmin tab="requests" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "signature-requests"],
				},
				loader: signatureRequestsLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "super-user-admin/signature-requests",
				element: <SignatureRequestsAdmin tab="requests" isSuperUserAdmin={true} />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "signature-requests"],
				},
				loader: signatureRequestsLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "administration/roles/create",
				element: <RolesAdmin tab="create" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.HIDE_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "roles"],
				},
				loader: rolesLoader,
			},
			{
				path: "administration/roles/:roleId/edit",
				element: <RolesAdmin tab="edit" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.HIDE_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "roles"],
				},
				loader: rolesLoader,
			},
			{
				path: "administration/roles/:roleId",
				element: <RolesAdmin tab="detail" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "roles"],
				},
				loader: rolesLoader,
			},
			{
				path: "administration/roles/:roleId/add-employees",
				element: <RolesAdmin tab="add-employees" />,
				handle: {
					// Focused workflow — hide the admin sidebar (matches create/edit chrome).
					tags: [RouterTags.NO_FEEDBACK, RouterTags.HIDE_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "roles"],
				},
				loader: rolesLoader,
			},
			{
				path: "administration/workflows",
				element: <WorkflowsAdmin tab="workflows" />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "workflows"],
				},
				loader: workflowsLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "super-user-admin/workflows",
				element: <WorkflowsAdmin tab="workflows" requireSuperuser />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "workflows"],
				},
				loader: workflowsLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "edit-review-template/:templateID?",
				element: <AdminReviewTemplateDetail />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "reviews", "templates"],
				},
			},
			{
				path: "super-user-admin/billing/:companyID",
				element: <Billing />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "billing"],
				},
			},
			{
				path: "administration/billing",
				element: <Billing />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "billing"],
				},
				loader: billingLoader,
				errorElement: <BillingErrorPage />,
			},
			{
				path: "administration/compensation",
				element: <CompensationAdmin />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "compensation"],
				},
				loader: compensationSettingsLoader,
			},
			{
				path: "super-user-admin/compensation/:companyID",
				element: <CompensationAdmin />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "compensation"],
				},
				loader: compensationSettingsLoader,
			},
			{
				path: "compensation/create",
				element: <CompensationCreateWizard />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.NO_ASK_PERFORMYARD],
					navPath: [MainNavSections.COMPENSATION, "create"],
				},
				loader: compensationCreateLoader,
			},
			{
				path: "administration/meetings-integrations",
				element: <MeetingsIntegrations />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "meetings-integrations"],
				},
				loader: meetingsIntegrationsSettingsLoader,
			},
			{
				path: "administration/content-library",
				element: <TemplateLibraryPage />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
				},
			},
			{
				path: "super-user-admin/:path/*",
				element: null,
				handle: {
					tags: [
						RouterTags.SETTINGS,
						RouterTags.NO_FEEDBACK,
						RouterTags.USES_TEMPLATE,
						RouterTags.SHOW_ADMIN_NAV,
					],
				},
			},
			{ path: "group/employees/:groupId", element: <ViewGroup />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "/doc-signing/*",
				element: null,
				handle: { tags: [RouterTags.NO_FEEDBACK, RouterTags.NO_ASK_PERFORMYARD, RouterTags.USES_TEMPLATE] },
			},
			{
				path: "edit-workflow/:workflowTemplateID?",
				element: <WorkflowTemplateEditor />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.HIDE_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "workflows"],
				},
			},
			{
				path: "launch-workflow",
				element: <LaunchWorkflow />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.NO_ASK_PERFORMYARD],
					navPath: [MainNavSections.COMPANY, "workflows"],
				},
			},
			{
				path: "company/workflows",
				element: <AllWorkflows />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					navPath: [MainNavSections.COMPANY, "workflows"],
				},
			},
			{
				path: "company/workflows/todos-report",
				element: <AllTodos />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					navPath: [MainNavSections.COMPANY, "workflows"],
				},
			},
			{
				path: "company/workflows/:workflowId",
				element: <WorkflowDetails />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					navPath: [MainNavSections.COMPANY, "workflows"],
				},
			},
			{ path: "jobs", element: <ViewAllJobs />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "jobs/:jobID",
				element: <ViewJob />,
				handle: { tags: [RouterTags.NO_FEEDBACK], legacyPageName: "ViewJob" },
			},
			{
				path: "goal/wizard",
				element: <GoalsCreate />,
				handle: { tags: [RouterTags.NO_FEEDBACK], action: "create", legacyPageName: "CreateGoal" },
			},
			{
				path: "goal/:goalID/copy",
				element: <GoalsCreate />,
				handle: { tags: [RouterTags.NO_FEEDBACK], action: "copy", legacyPageName: "CopyGoal" },
			},
			{
				path: "goal/:goalID/edit",
				element: <GoalsCreate />,
				handle: { tags: [RouterTags.NO_FEEDBACK], action: "edit", legacyPageName: "EditGoal" },
			},
			{ path: "goal/build/:jobID", element: <ViewCreateGoalsJob /> },
			{
				path: "integrations/:page?",
				element: <IntegrationsMain />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.NO_ASK_PERFORMYARD, RouterTags.SHOW_ADMIN_NAV],
					legacyPageName: "IntegrationsSetup",
					navPath: [MainNavSections.ADMINISTRATION, "integrations"],
				},
			},

			{
				path: "company/goals/:page?/:goal_id?",
				element: <CompanyGoalsPage />,
				handle: {
					tags: [],
					navPath: [MainNavSections.COMPANY, "goals"],
				},
			},
			{
				path: "goal/:goal_id?",
				element: <SingleGoal />,
				handle: { navPath: [MainNavSections.PERFORMANCE, "goals"] },
			},
			{
				path: "company/goal/:goal_id?",
				element: <SingleGoal />,
				handle: { navPath: [MainNavSections.COMPANY, "goals"] },
			},
			{ path: "extend-session", element: <ExtendSessionPage /> },
			{
				path: "settings/:page?",
				element: <UserSettings />,
				loader: async ({ params }) => {
					if (params.page === "meetings-integrations") {
						return meetingsIntegrationsViewLoader();
					}
					return null;
				},
			},
			{
				path: "administration/notifications",
				element: <NotificationsTemplate />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "notifications"],
				},
			},
			{
				path: "employees/:employeeID/goals/:page?/",
				element: <EmployeeGoals />,
				handle: { navPath: [MainNavSections.PERFORMANCE, "goals"] },
			},
			{
				path: "employees/:employeeID/competencies/:page?/",
				element: <EmployeeCompetencies />,
				handle: { navPath: [MainNavSections.PERFORMANCE, "competencies"] },
			},
			{
				path: "employees/goals",
				element: <EmployeeGoals />,
				handle: { navPath: [MainNavSections.PERFORMANCE, "goals"] },
			},
			{
				path: "company/reviews/graphs/",
				element: <PerformanceGraphsWrapper />,
				handle: {
					tags: [RouterTags.USES_TEMPLATE_AND_REACT],
					navPath: [MainNavSections.COMPANY, "reviews"],
				},
			},
			{
				path: "company/reviews/:page?/",
				element: <CompanyReviews />,
				handle: { navPath: [MainNavSections.COMPANY, "reviews"] },
			},
			{
				path: "company/competencies/:page?/",
				element: <CompanyCompetencies />,
				handle: { navPath: [MainNavSections.COMPANY, "competencies"] },
			},

			{ path: "start-review/:paramA?/", element: null, handle: { tags: [RouterTags.USES_TEMPLATE] } },

			{
				path: "org-chart",
				element: <OrgChartRedirect />,
			},
			{
				path: "company/feedback",
				element: <CompanyFeedback />,
				handle: { navPath: [MainNavSections.COMPANY, "feedback"] },
			},
			{ path: "company/reports/all", element: null, handle: { navPath: [MainNavSections.COMPANY, "reports"] } },
			{
				path: "company/reports/collected",
				element: null,
				handle: { navPath: [MainNavSections.COMPANY, "reports"] },
			},
			{ path: "reviews", element: <UserReviews /> },
			{
				path: "reviews/:paramA?/:paramB?",
				element: <UserReviews />,
				handle: {
					tags: [RouterTags.USER_PAGE],
					navPath: [MainNavSections.PERFORMANCE, "reviews"],
				},
			},
			{
				path: "view-review/:reviewID",
				element: <ViewReview />,
				handle: { legacyPageName: "ViewReview", tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{
				path: "view-review/:reviewID/awaiting-approval",
				element: <ViewReview />,
				handle: {
					legacyPageName: "ViewReviewAwaitingApproval",
					tags: [RouterTags.USES_TEMPLATE_AND_REACT],
				},
			},
			{ path: "view-field-history/:fieldID/:userID", element: <ViewFieldHistory /> },
			{
				path: "details/:userID",
				element: <EmployeeDetailsLayout />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.ANALYTICS] },
				loader: viewEmployeeDetailsLoader,
				shouldRevalidate: revalidateOnPathOrTargetCompanyChange,
				errorElement: <EmployeeDetailsErrorPage />,
				children: [
					{ index: true, element: <EmployeeDetailsView /> },
					{ path: "insights", element: <EmployeeInsights /> },
				],
			},
			{
				path: "administration/reviews/forms/",
				element: <AdminReviewsForms />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "reviews", "forms"],
				},
				loader: adminReviewsFormsLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "edit-review-form/:baseFormId",
				element: <EditForm />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.FORM_BUILDER],
					navPath: [MainNavSections.ADMINISTRATION, "reviews", "forms", "edit-review-form"],
				},
				loader: adminReviewFormLoader,
			},
			{
				path: "super-user-admin/reviews/forms/:companyId",
				element: <AdminReviewsForms />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.SHOW_ADMIN_NAV],
					navPath: [MainNavSections.ADMINISTRATION, "reviews", "forms"],
				},
				loader: adminReviewsFormsLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "/edit-employee/:userID",
				element: <EmployeeDetailsLayout />,
				loader: viewEmployeeDetailsLoader,
				shouldRevalidate: revalidateOnPathOrTargetCompanyChange,
				errorElement: <EmployeeDetailsErrorPage />,
				children: [{ index: true, element: <EmployeeDetailsView /> }],
			},
			{ path: "view-field-history/:fieldID/:userID", element: <EditEmployee /> },
			{
				path: "add-employee",
				element: <EditEmployee />,
				loader: addEmployeeLoader,
				handle: { tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{
				path: "add-employee/:companyID",
				element: <EditEmployee />,
				loader: addEmployeeLoader,
				handle: { tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{
				path: "analytics/report/:reportID?",
				element: <AnalyticsReport />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.ANALYTICS] },
			},
			{ path: "analytics", element: <ReportAnalytics />, handle: { navPath: [MainNavSections.ANALYTICS] } },
			{
				path: "analytics/chat",
				element: <ReportAnalyticsChat />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.ANALYTICS] },
			},
			{
				path: "engagement/overview",
				element: <EngagementOverview />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					navPath: [MainNavSections.ENGAGEMENT, "overview"],
				},
			},
			{
				path: "engagement/cohorts",
				element: <EngagementCohorts />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					navPath: [MainNavSections.ENGAGEMENT, "cohorts"],
				},
			},
			{
				path: "engagement/factors",
				element: <EngagementFactors />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					navPath: [MainNavSections.ENGAGEMENT, "factors"],
				},
			},
			{
				path: "engagement/enps",
				element: <EngagementEnps />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					navPath: [MainNavSections.ENGAGEMENT, "enps"],
				},
			},
			{
				path: "engagement/questions",
				element: <EngagementQuestions />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					navPath: [MainNavSections.ENGAGEMENT, "questions"],
				},
			},
			{ path: "documents", element: <Documents />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "documents/:userID",
				element: <Documents />,
				handle: {
					tags: [RouterTags.USER_PAGE],
					navPath: [MainNavSections.PERFORMANCE, "documents"],
				},
			},
			{
				path: "company/documents",
				element: <Documents />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.COMPANY, "documents"] },
			},
			{
				path: "company/resources",
				element: <Navigate to="/company/resources/documents" replace />,
			},
			{
				path: "company/links",
				element: <Navigate to="/company/resources/links" replace />,
			},
			{
				path: "company/resources/documents",
				element: <CompanyResources />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.COMPANY, "resources"] },
			},
			{
				path: "company/resources/links",
				element: <CompanyResources />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.COMPANY, "resources"] },
			},
			{
				path: "review/:reviewID/",
				element: <ViewReviewForm />,
				handle: { tags: [RouterTags.USES_TEMPLATE_AND_REACT, RouterTags.FORM_AUTHORING] },
			},
			{
				path: "iframe/review/:reviewID/",
				element: <Review />,
				handle: { tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{
				path: "/meeting-workspaces",
				element: <WorkspacesTable />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					activeNavigation: "meeting-workspaces",
					navPath: [MainNavSections.MEETING_WORKSPACES],
				},
				loader: workspacesTableLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "/meeting-workspaces/:workspaceId",
				element: <MeetingsTable />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.MEETING_WORKSPACES] },
			},
			{
				path: "/meeting-workspaces/:workspaceId/meetings/:meetingId",
				element: <MeetingDetails />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.MEETING_WORKSPACES] },
				loader: meetingDetailsLoader,
			},
			{
				path: "company/meetings",
				element: <CompanyMeetingsPage />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					activeNavigation: "company-meetings",
					navPath: [MainNavSections.COMPANY, "meetings"],
				},
			},
			{
				path: "company/meetings/coverage",
				element: <CompanyMeetingsCoveragePage />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
					activeNavigation: "company-meetings",
					navPath: [MainNavSections.COMPANY, "meetings"],
				},
			},
			{
				path: "surveys",
				element: <SurveysPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.SURVEYS] },
				loader: surveysLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "surveys/my-surveys",
				element: <SurveysPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.SURVEYS] },
				loader: surveysLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "surveys/my-teams-surveys",
				element: <SurveysPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.SURVEYS] },
				loader: surveysLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				// allow query params to be passed to the survey details page
				path: "surveys/:surveyId",
				element: <SurveyDetailsPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.SURVEYS] },
				loader: surveyDetailsLoader,
				shouldRevalidate: revalidateOnlyOnPathChange,
			},
			{
				path: "survey/:surveyLinkId",
				element: <SurveyPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK], navPath: [MainNavSections.SURVEYS] },
				loader: surveyLoader,
			},
			{
				path: "compensation",
				element: <CompensationLanding />,
				handle: {
					navPath: [MainNavSections.COMPENSATION],
				},
			},
			{
				path: "compensation/cycles/:cycleId",
				element: <CycleDetailsLayout />,
				handle: {
					navPath: [MainNavSections.COMPENSATION],
				},
				children: [
					{ index: true, element: <></> },
					{ path: "my-decisions", element: <MyDecisions /> },
					{ path: "cycle-overview", element: <CycleOverview /> },
					{ path: "budget-reviews", element: <BudgetReviews /> },
				],
			},
		],
	},
] as RouteObject[];

export default routes;
